import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import gql from 'graphql-tag';
import { useLazyQuery, useMutation } from '@vue/apollo-composable';
import { useStore } from '~/store';
import debounce from '~/helpers/debounce';
import useSnackbarQueue from '~/composables/useSnackbarQueue';
import RouterHelper from '~/helpers/router';

const MAX_FAVORITES = 10;
const DEBOUNCE_DELAY = 1000;

// GraphQL query to fetch user's favorite items
const GET_FAVORITES = gql`
    query GetMyFavorites {
        me {
            favorites
        }
    }
`;

// GraphQL mutation for updating user's favorite items
const UPDATE_FAVORITES = gql`
    mutation UpdateMyFavourites($favorites: [String!]!) {
        updateMyFavourites(input: { favorites: $favorites }) {
            favorites
        }
    }
`;

/**
 * Favorites composable
 */
export default function useFavorites() {
  const store = useStore();

  /**
   * Get/set the user's favorite URLs.
   */
  const userFavorites = computed({
    get() {
      return store?.state?.favorites?.favorites ?? [];
    },
    set(favorites) {
      if (!Array.isArray(favorites)) {
        return;
      }

      store.commit('favorites/commitFavorites', favorites);
    },
  });

  const { addSnackbar } = useSnackbarQueue();
  const { t } = useI18n();

  const resolveUrl = (input) => {
    if (typeof input === 'string') return input;
    if (typeof input === 'object' && input?.name) {
      return RouterHelper.resolvePathFromName(input.name, input.params || {});
    }
    return null;
  };

  /**
   * Toggles a URL in the user's favorites list.
   */
  const toggleFavorite = (input) => {
    const url = resolveUrl(input);
    if (!url) {
      console.warn('Invalid URL for :', input);
      return;
    }
    const index = userFavorites.value.indexOf(url);
    const favCopy = [...userFavorites.value];
    if (index !== -1) {
      favCopy.splice(index, 1);
    } else if (favCopy.length < MAX_FAVORITES) {
      favCopy.push(url);
    } else {
      addSnackbar({
        content: t('navigation.maxFavoritesReached'),
        color: 'info',
        timeout: 2500,
      });
    }
    userFavorites.value = favCopy;
  };

  /**
   * Checks if a given URL is marked as a favorite.
   * @param {string} url - The URL to check.
   * @returns {boolean} - True if the URL is a favorite, false otherwise.
   */
  const isFavorite = (url) => {
    if (typeof url !== 'string') {
      return false;
    }

    return userFavorites.value.some((fav) => fav.toLowerCase() === url.toLowerCase());
  };

  // Fetches the user's favorites using a lazy query
  const {
    load: loadFavorites,
    onResult: onFavoritesResult,
    loading: loadingFavorites,
  } = useLazyQuery(GET_FAVORITES, {}, {
    fetchPolicy: 'no-cache',
    manual: true,
  });

  // Mutation to update the user's favorites
  const {
    mutate: updateFavorites,
  } = useMutation(UPDATE_FAVORITES);

  const debouncedUpdateFavorites = debounce(
    async (favoritesToUpdate) => {
      await updateFavorites({
        favorites: favoritesToUpdate,
      });
    },
    DEBOUNCE_DELAY,
  );

  return {
    debouncedUpdateFavorites,
    isFavorite,
    loadFavorites,
    loadingFavorites,
    onFavoritesResult,
    toggleFavorite,
    userFavorites,
  };
}
